import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, FileUp, HeartPulse, Plus, Sparkles, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";

/** SEO admin — Part A: redirect manager + 404 monitor. (Health tab arrives with Part G.) */
export default function SeoRedirects() {
  const [tab, setTab] = useState("redirects");
  const [redirects, setRedirects] = useState([]);
  const [notFounds, setNotFounds] = useState([]);
  const [health, setHealth] = useState(null);
  const [form, setForm] = useState({ from_path: "", to_path: "", match_type: "exact" });
  const [csv, setCsv] = useState("");
  const [resolveTarget, setResolveTarget] = useState({});

  const load = useCallback(() => {
    api.get("/admin/redirects").then((r) => setRedirects(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/seo/404s").then((r) => setNotFounds(r.data)).catch(() => {});
    api.get("/admin/seo/health").then((r) => setHealth(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const create = async () => {
    try {
      const r = await api.post("/admin/redirects", form);
      toast.success(`Redirect created${r.data.chains ? " — WARNING: target itself redirects (chain)" : ""}`);
      setForm({ from_path: "", to_path: "", match_type: "exact" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const bulk = async () => {
    try {
      const r = await api.post("/admin/redirects/bulk", { csv });
      toast.success(`${r.data.created} created, ${r.data.skipped} skipped${r.data.errors.length ? `, ${r.data.errors.length} errors` : ""}`);
      if (r.data.errors.length) toast.warning(r.data.errors.slice(0, 3).join(" · "));
      setCsv("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const seedShopify = async () => {
    const r = await api.post("/admin/redirects/seed-shopify");
    toast.success(`${r.data.created} Shopify legacy rules created`);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this redirect?")) return;
    await api.delete(`/admin/redirects/${id}`);
    load();
  };

  const resolve404 = async (id) => {
    const to = resolveTarget[id];
    if (!to) return toast.error("Enter a target path first");
    try {
      const r = await api.post(`/admin/seo/404s/${id}/resolve`, { to_path: to });
      toast.success(`Redirect created: ${r.data.redirect.from} → ${r.data.redirect.to}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const dismiss404 = async (id) => {
    await api.delete(`/admin/seo/404s/${id}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="seo-admin-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">SEO — Migration & Redirects</h1>
          <p className="text-sm text-neutral-500">Shopify URLs keep working after the move to siyaara.com. One hop, always.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab("redirects")} className={`px-3 py-2 text-xs rounded ${tab === "redirects" ? "bg-neutral-900 text-white" : "border"}`} data-testid="tab-redirects">Redirects ({redirects.length})</button>
          <button onClick={() => setTab("404s")} className={`px-3 py-2 text-xs rounded ${tab === "404s" ? "bg-neutral-900 text-white" : "border"}`} data-testid="tab-404s">404 monitor ({notFounds.length})</button>
          <button onClick={() => setTab("health")} className={`px-3 py-2 text-xs rounded flex items-center gap-1 ${tab === "health" ? "bg-neutral-900 text-white" : "border"}`} data-testid="tab-health"><HeartPulse size={12} /> Health</button>
        </div>
      </div>

      {tab === "redirects" && (
        <>
          <section className="bg-white border rounded p-4 space-y-3" data-testid="redirect-create">
            <div className="flex flex-wrap items-end gap-2 text-xs">
              <div><label className="block text-neutral-500 mb-1">From path</label>
                <input value={form.from_path} onChange={(e) => setForm({ ...form, from_path: e.target.value })} placeholder="/old/page or /prefix/* or ^/regex$" className="border rounded px-2 py-1.5 w-64 font-mono" data-testid="redirect-from" /></div>
              <div><label className="block text-neutral-500 mb-1">To path</label>
                <input value={form.to_path} onChange={(e) => setForm({ ...form, to_path: e.target.value })} placeholder="/new/page ($1 for regex groups)" className="border rounded px-2 py-1.5 w-64 font-mono" data-testid="redirect-to" /></div>
              <div><label className="block text-neutral-500 mb-1">Match</label>
                <select value={form.match_type} onChange={(e) => setForm({ ...form, match_type: e.target.value })} className="border rounded px-2 py-1.5" data-testid="redirect-match">
                  <option value="exact">exact</option><option value="prefix">prefix (…*)</option><option value="regex">regex</option>
                </select></div>
              <button onClick={create} className="px-4 py-2 bg-neutral-900 text-white rounded font-medium" data-testid="redirect-create-btn"><Plus size={13} className="inline mr-1" />Add redirect</button>
              <button onClick={seedShopify} className="px-4 py-2 border rounded text-xs flex items-center gap-1.5" data-testid="seed-shopify-btn"><Sparkles size={13} /> Seed Shopify legacy rules</button>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-neutral-500 flex items-center gap-1"><FileUp size={12} /> Bulk CSV import (from,to[,match])</summary>
              <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={5} className="w-full border rounded p-2 mt-2 font-mono" placeholder={"/collections/rings/products/old-ring,/products/old-ring\n/old-page,/shop\n/blog/*,/guides,prefix"} data-testid="redirect-csv" />
              <button onClick={bulk} className="mt-2 px-3 py-1.5 bg-neutral-900 text-white rounded" data-testid="redirect-bulk-btn">Import CSV</button>
            </details>
          </section>

          <section className="bg-white border rounded overflow-x-auto" data-testid="redirects-table">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 text-neutral-500"><tr>{["From", "→", "To", "Match", "Hits", "Source", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr></thead>
              <tbody className="divide-y">
                {redirects.map((r) => (
                  <tr key={r.id} data-testid={`redirect-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono">{r.from_path}</td>
                    <td className="px-1"><ArrowRight size={12} className="text-neutral-400" /></td>
                    <td className="px-3 py-2 font-mono">{r.to_path}</td>
                    <td className="px-3 py-2">{r.match_type}</td>
                    <td className="px-3 py-2">{r.hits}</td>
                    <td className="px-3 py-2 text-neutral-400">{r.note || "manual"}</td>
                    <td className="px-3 py-2 text-right"><button onClick={() => del(r.id)} className="text-red-500 hover:text-red-700" data-testid={`redirect-del-${r.id}`}><Trash2 size={13} /></button></td>
                  </tr>
                ))}
                {!redirects.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-neutral-400">No redirects yet — seed the Shopify legacy rules to start.</td></tr>}
              </tbody>
            </table>
          </section>
        </>
      )}

      {tab === "health" && health && (
        <section className="space-y-4" data-testid="seo-health">
          <div className="grid md:grid-cols-4 gap-3 text-xs">
            {[
              ["Pages missing meta", health.missing_meta.length],
              ["Duplicate titles", health.duplicate_titles.length],
              ["Thin pages (<3 products)", health.thin_pages.length],
              ["Unresolved 404s", health.unresolved_404s],
              ["Orphan pages", health.orphan_pages.length],
              ["Broken internal links", health.broken_internal_links.length],
              ["Indexable facet pages", health.counts.visible_facets],
              ["Active products", health.counts.products],
            ].map(([label, val]) => (
              <div key={label} className={`bg-white border rounded p-4 ${val > 0 && !String(label).includes("Indexable") && !String(label).includes("Active") ? "border-amber-300" : ""}`}>
                <div className="text-neutral-500">{label}</div>
                <div className="text-lg font-semibold">{val}</div>
              </div>
            ))}
          </div>
          {[
            ["Missing meta", health.missing_meta, (x) => `${x.type}: ${x.title} — missing ${x.missing.join(", ")}`],
            ["Duplicate titles", health.duplicate_titles, (x) => `"${x.title}" on ${x.pages.length} pages`],
            ["Thin pages (auto-suppressed, noindex)", health.thin_pages, (x) => `${x.title} — ${x.count} in stock`],
            ["Orphan pages (no internal links)", health.orphan_pages, (x) => `${x.page} — ${x.reason}`],
            ["Broken internal links", health.broken_internal_links, (x) => `${x.label} → ${x.url} (${x.issue})`],
          ].map(([title, rows, fmt]) => (
            <div key={title} className="bg-white border rounded p-4">
              <b className="text-sm">{title} ({rows.length})</b>
              <div className="mt-2 text-xs text-neutral-600 max-h-44 overflow-y-auto divide-y divide-neutral-100">
                {rows.map((r, i) => <div key={i} className="py-1.5">{fmt(r)}</div>)}
                {!rows.length && <div className="py-2 text-neutral-400">None. ✅</div>}
              </div>
            </div>
          ))}
        </section>
      )}

      {tab === "404s" && (
        <section className="bg-white border rounded overflow-x-auto" data-testid="notfound-table">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 text-neutral-500"><tr>{["Path", "Hits", "Last referrer", "Last seen", "Redirect to…", "", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr></thead>
            <tbody className="divide-y">
              {notFounds.map((n) => (
                <tr key={n.id} data-testid={`404-row-${n.id}`}>
                  <td className="px-3 py-2 font-mono">{n.path}</td>
                  <td className="px-3 py-2 font-semibold">{n.hits}</td>
                  <td className="px-3 py-2 text-neutral-500 max-w-40 truncate">{n.last_referrer || "—"}</td>
                  <td className="px-3 py-2 text-neutral-400">{n.last_at ? new Date(n.last_at).toLocaleDateString("en-IN") : ""}</td>
                  <td className="px-3 py-2"><input value={resolveTarget[n.id] || ""} onChange={(e) => setResolveTarget({ ...resolveTarget, [n.id]: e.target.value })} placeholder="/shop" className="border rounded px-2 py-1 w-40 font-mono" data-testid={`404-target-${n.id}`} /></td>
                  <td className="px-3 py-2"><button onClick={() => resolve404(n.id)} className="px-2.5 py-1 bg-brand text-white rounded" data-testid={`404-resolve-${n.id}`}>Create redirect</button></td>
                  <td className="px-3 py-2 text-right"><button onClick={() => dismiss404(n.id)} className="text-neutral-400 hover:text-red-600" data-testid={`404-dismiss-${n.id}`}><Trash2 size={13} /></button></td>
                </tr>
              ))}
              {!notFounds.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-neutral-400">No unresolved 404s logged. 🎉</td></tr>}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
